var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import { Centrifuge } from 'centrifuge';
import { useAppDispatch } from './reduxHooks';
import { setMatchState } from '../store/match';
import { get } from '../api/api';
const CENTRIFUGO_URL = process.env.CENTRIFUGO_URL || '/connection/websocket';
function fetchGmWsToken() {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield get({ path: '/v1/centrifugo/token' });
        return response.token;
    });
}
export function useGmCentrifugo(matchId, onTrigger) {
    const dispatch = useAppDispatch();
    const centrifugeRef = useRef(null);
    const subRef = useRef(null);
    const [connected, setConnected] = useState(false);
    // Connect once on mount, disconnect on unmount
    useEffect(() => {
        let cancelled = false;
        function connect() {
            return __awaiter(this, void 0, void 0, function* () {
                let token;
                try {
                    token = yield fetchGmWsToken();
                }
                catch (e) {
                    console.error('[useGmCentrifugo] Failed to fetch WS token', e);
                    return;
                }
                if (cancelled)
                    return;
                const centrifuge = new Centrifuge(CENTRIFUGO_URL, {
                    token,
                    getToken: () => __awaiter(this, void 0, void 0, function* () {
                        const t = yield fetchGmWsToken();
                        return t;
                    }),
                });
                centrifuge.on('connected', () => {
                    if (!cancelled)
                        setConnected(true);
                });
                centrifuge.on('disconnected', () => {
                    if (!cancelled)
                        setConnected(false);
                });
                centrifugeRef.current = centrifuge;
                centrifuge.connect();
            });
        }
        connect();
        return () => {
            var _a;
            cancelled = true;
            (_a = centrifugeRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            centrifugeRef.current = null;
        };
    }, []);
    // Subscribe to gm:{matchId} channel when connected and matchId is available
    useEffect(() => {
        const centrifuge = centrifugeRef.current;
        if (!centrifuge || !matchId)
            return;
        const channel = `gm:${matchId}`;
        const trySubscribe = () => {
            var _a;
            // Clean up previous subscription if any
            if (subRef.current) {
                subRef.current.unsubscribe();
                subRef.current.removeAllListeners();
                (_a = centrifugeRef.current) === null || _a === void 0 ? void 0 : _a.removeSubscription(subRef.current);
                subRef.current = null;
            }
            const existing = centrifuge.getSubscription(channel);
            if (existing) {
                existing.unsubscribe();
                existing.removeAllListeners();
                centrifuge.removeSubscription(existing);
            }
            const sub = centrifuge.newSubscription(channel);
            sub.on('publication', (ctx) => {
                var _a, _b;
                console.log('[useGmCentrifugo] raw publication ctx.data:', JSON.stringify(ctx.data));
                const payload = ctx.data;
                if (!(payload === null || payload === void 0 ? void 0 : payload.event)) {
                    console.warn('[useGmCentrifugo] no event field in payload, skipping');
                    return;
                }
                try {
                    if (payload.event === 'gm_state_updated' && ((_a = payload.data) === null || _a === void 0 ? void 0 : _a.matchState)) {
                        console.log('[useGmCentrifugo] dispatching gm_state_updated');
                        dispatch(setMatchState(payload.data.matchState));
                    }
                    else if (payload.event === 'gm_notification' && ((_b = payload.data) === null || _b === void 0 ? void 0 : _b.message)) {
                        console.log('[useGmCentrifugo] calling onTrigger:', payload.data.message);
                        onTrigger(payload.data.message);
                    }
                    else {
                        console.warn('[useGmCentrifugo] unhandled event:', payload.event);
                    }
                }
                catch (e) {
                    console.error('[useGmCentrifugo] Failed to handle publication', e);
                }
            });
            sub.on('error', (ctx) => {
                console.error('[useGmCentrifugo] Subscription error', ctx);
            });
            sub.subscribe();
            subRef.current = sub;
        };
        if (connected) {
            trySubscribe();
        }
        else {
            const handler = () => trySubscribe();
            centrifuge.on('connected', handler);
            return () => {
                centrifuge.removeListener('connected', handler);
            };
        }
        return () => {
            var _a;
            if (subRef.current) {
                subRef.current.unsubscribe();
                subRef.current.removeAllListeners();
                (_a = centrifugeRef.current) === null || _a === void 0 ? void 0 : _a.removeSubscription(subRef.current);
                subRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchId, connected]);
}
